import React from 'react';
import PropTypes from 'prop-types';
import { NumberField } from 'common/Forms';
import { GradeByType } from '@/components/staffer/NotebookGradeByMenu/types';
import useFlags from '@/hooks/useFlags';
import { SelectField, optionList, Header } from '../Util';
import Text from '../../../../ui/Text';
import Tooltip, { TCITooltip } from '../../../../ui/Tooltip';
import Icon from '../../../../ui/Icon';
import styles from './GradesForm.module.scss';

const validateNotesGrade = (value) => {
  let error = false;

  if (isNaN(value)) {
    error = true;
  }
  else if (value > 100 || value < 1) {
    error = true;
  }
  else if (value % 1 !== 0) {
    error = true;
  }

  return error ? 'Default points value is invalid. Must be an integer value from 1 to 100' : null;
};

const GradesForm = ({ form, usesAssignmentsBasedGrading = useFlags().usesAssignmentsBasedGrading }) => {

  const gradeByOptions = [
    { label: 'Section', value: GradeByType.Section },
    { label: 'Lesson', value: GradeByType.Lesson },
    { label: 'Assignment', value: GradeByType.Assignment }
  ];

  const headerText = 'Default Grading for Reading Assignments';

  const gradeByFieldLabel = 'Reading Assignment Grading Preference';

  const gradeByFieldTooltip = 'Update this setting to apply grading by lesson, section, or assignment for all notebooks.'

  const lessonTooltip = `Changing this setting will update the max possible score of all
    notebooks, except for notebook lesson scores that you have already set on the Grades page.`;
  const sectionTooltip = `Changing this setting will update the max possible score of all
    notebooks, except for notebook section scores that you have already set on the Grades page.`;

  const viewGradesByOptions = [
    { label: 'Score', value: false },
    { label: 'Percentage', value: true },
  ];

  return (
    <>
      {usesAssignmentsBasedGrading && (
        <>
          <Header text="Select Your Default Grading View" />

          <SelectField
            label="View Grades By"
            labelClass="tw-w-1/2"
            name="show_percentages"
            options={viewGradesByOptions}
            sectionHeader="tw-inline"
            tooltip="Choose whether scores display as raw points or as a percentage of the total possible points."
            onChange={form.mutators.handleSelect}
          />

          <hr />
        </>
      )}

      <Header text={headerText} />

      {!usesAssignmentsBasedGrading && (
        <SelectField
          label={gradeByFieldLabel}
          labelClass="tw-w-1/2"
          name="grade_notebooks_by"
          options={gradeByOptions}
          sectionHeader="tw-inline"
          tooltip={gradeByFieldTooltip}
          onChange={form.mutators.handleSelect}
        />
      )}

      {!usesAssignmentsBasedGrading && (
        <span className={styles.numberFieldContainer}>
          <div className="tw-flex tw-items-center tw-w-1/2">
            <Text asChild size="sm" variant="label">
              <label htmlFor="notebook_possible_points_field">Default Score for &quot;Grade by Lesson&quot;</label>
            </Text>
            <TCITooltip>
              <Tooltip.Trigger asChild>
                <div>
                  <Icon className="tw-ml-1" icon="InfoAlternate" size={14} />
                </div>
              </Tooltip.Trigger>
              <Tooltip.Content>
                {lessonTooltip}
              </Tooltip.Content>
            </TCITooltip>
          </div>
          <NumberField
            hideLabel
            useTextComponent
            endText={<Text size="sm">per lesson</Text>}
            name="notebook_possible_points"
            tooltipText={lessonTooltip}
            validate={validateNotesGrade}
          />
        per lesson
        </span>
      )}

      <span className={styles.numberFieldContainer}>
        <div className="tw-flex tw-items-center tw-w-1/2">
          <Text asChild size="sm" variant="label">
            <label htmlFor="section_possible_points_field">
              {usesAssignmentsBasedGrading && 'Default Points per Section'}
              {!usesAssignmentsBasedGrading && 'Default Score for "Grade by Section"'}
            </label>
          </Text>
          <TCITooltip>
            <Tooltip.Trigger asChild>
              <div>
                <Icon className="tw-ml-1" icon="InfoAlternate" size={14} />
              </div>
            </Tooltip.Trigger>
            <Tooltip.Content>
              {sectionTooltip}
            </Tooltip.Content>
          </TCITooltip>
        </div>
        <div className="tw-flex tw-items-center">
          <NumberField
            hideLabel
            useTextComponent
            name="section_possible_points"
            tooltipText={sectionTooltip}
            validate={validateNotesGrade}
          />
        per section
        </div>
      </span>

      <div className="tw-mt-5">
        <Header text="Select what results you want highlighted in Grades Page" />
      </div>

      <SelectField
        label="Class Trend Threshold"
        name="trends_highlight"
        options={optionList(0, 100, 5, '%')}
        tooltip="Highlights questions with a class average lower than the percentage you set."
        onChange={form.mutators.handleSelect}
      />

      <SelectField
        label="Low Score Threshold"
        name="student_challenge_highlight"
        options={[{ label: 'None', value: -1 }, ...optionList(0, 100, 5, '%')]}
        tooltip="Highlights individual scores lower than the percentage you set."
        onChange={form.mutators.handleSelect}
      />
    </>
  );
};

GradesForm.propTypes = {
  form: PropTypes.shape({
    mutators: PropTypes.shape({
      handleSelect: PropTypes.func
    })
  }).isRequired,
  usesAssignmentsBasedGrading: PropTypes.bool,
};

export default GradesForm;
